import React from 'react';
import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  MapPin,
  Search,
  ShieldCheck,
  UserRoundCheck,
  WalletCards,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import Header from '../../components/Header';

const CLIENT_STEPS = [
  { icon: Search, title: 'Choose a service', text: 'Select the type of help you need and enter your service location.' },
  { icon: CalendarDays, title: 'Choose date & time', text: 'Pick a suitable date and choose from available booking times.' },
  { icon: UserRoundCheck, title: 'Select a Gigger', text: 'Review available Giggers, profile details, actual rates and availability.' },
  { icon: ClipboardCheck, title: 'Submit the booking', text: 'Review your details and send the booking request to your selected Gigger.' },
  { icon: MapPin, title: 'Track & manage', text: 'Use your tracking link to follow updates and manage the booking as it progresses.' },
  { icon: BadgeCheck, title: 'Optional account', text: 'Create a client account if you want a dashboard for future bookings and account details.' },
];

const GIGGER_STEPS = [
  { icon: BriefcaseBusiness, title: 'Create an account', text: 'Complete the guided five-step registration with your profile, skills and availability.' },
  { icon: ShieldCheck, title: 'Complete verification', text: 'Upload the two required documents and wait for Giggs review.' },
  { icon: Search, title: 'Browse & apply', text: 'Review suitable opportunities and choose Gigs that fit your schedule.' },
  { icon: UserRoundCheck, title: 'Connect with clients', text: 'Manage accepted bookings and communicate through the Giggs flow.' },
  { icon: WalletCards, title: 'Complete & get paid', text: 'Finish the Gig, complete the required steps and receive your payout.' },
];

const StepCard = ({ step, index }) => {
  const Icon = step.icon;
  return (
    <article className="group min-w-0 rounded-2xl bg-white p-5 shadow-[0_8px_28px_rgba(15,23,42,0.055)] ring-1 ring-black/[0.045] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_38px_rgba(15,23,42,0.09)] sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-700"><Icon size={21} /></span>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-700 text-[11px] font-semibold text-white">{index + 1}</span>
      </div>
      <h3 className="mb-0 mt-5 font-nunito text-lg font-bold text-slate-950">{step.title}</h3>
      <p className="mb-0 mt-2 text-sm font-normal leading-6 text-slate-600">{step.text}</p>
    </article>
  );
};

const HowItWorks = () => (
  <>
    <Header />
    <title>How Giggs Works | Clients and Giggers</title>
    <main className="w-full overflow-x-hidden bg-white font-dm text-slate-800">
      <section className="bg-gradient-to-b from-white via-green-50/60 to-white px-4 py-10 sm:px-6 sm:py-14 lg:px-8 lg:py-16 2xl:px-10">
        <div className="mx-auto grid w-full max-w-[1450px] items-center gap-9 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-12">
          <div className="min-w-0">
            <span className="text-xs font-medium uppercase tracking-[0.14em] text-green-700">How it works</span>
            <h1 className="mb-0 mt-3 font-nunito text-[clamp(2.25rem,5vw,4rem)] font-bold leading-[1.08] tracking-[-0.02em] text-slate-950">Simple for Clients.<br /><span className="text-green-600">Flexible for Giggers.</span></h1>
            <p className="mb-0 mt-5 max-w-2xl text-base font-normal leading-7 text-slate-600 lg:text-lg">Book trusted local help or find flexible work through a clear, guided process designed for both sides of every Gig.</p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link to="/book" className="rounded-xl bg-green-700 px-6 py-3.5 text-center text-sm font-semibold text-white no-underline shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-800 hover:shadow-lg">Book a Gig</Link>
              <Link to="/provider-register" className="rounded-xl bg-white px-6 py-3.5 text-center text-sm font-semibold text-slate-800 no-underline shadow-sm ring-1 ring-inset ring-slate-200 transition-all duration-200 hover:-translate-y-0.5 hover:ring-green-300 hover:shadow-md">Find a Gig</Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="overflow-hidden rounded-[1.75rem] shadow-[0_18px_45px_rgba(15,23,42,0.12)]">
              <video
                src="/gif/hand_made_jobs.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label="Client getting everyday help"
                className="aspect-[4/5] h-full w-full object-cover"
              />
            </div>
            <div className="grid gap-3 sm:gap-4">
              <div className="overflow-hidden rounded-[1.5rem] shadow-[0_14px_35px_rgba(15,23,42,0.1)]">
                <video
                  src="/gif/dog_walking.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label="Flexible scheduling for Giggers"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-[1.5rem] shadow-[0_14px_35px_rgba(15,23,42,0.1)]">
                <video
                  src="/gif/gradening.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label="Verified local providers"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F6FBF8] px-4 py-11 sm:px-6 sm:py-14 lg:px-8 lg:py-16 2xl:px-10">
        <div className="mx-auto w-full max-w-[1450px]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className="text-xs font-medium uppercase tracking-[0.14em] text-green-700">For clients</span>
              <h2 className="mb-0 mt-2 font-nunito text-[clamp(1.8rem,3.4vw,2.6rem)] font-bold tracking-[-0.015em] text-slate-950">Book first. Account creation is optional.</h2>
              <p className="mb-0 mt-3 max-w-3xl text-sm font-normal leading-6 text-slate-600 lg:text-base">You can submit, track and manage a booking without creating an account first. Create one later if you want a dashboard for future Gigs.</p>
            </div>
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-green-50 px-3 py-2 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-200"><CheckCircle2 size={15} /> No account required</span>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{CLIENT_STEPS.map((step, index) => <StepCard key={step.title} step={step} index={index} />)}</div>
        </div>
      </section>

      <section className="px-4 py-11 sm:px-6 sm:py-14 lg:px-8 lg:py-16 2xl:px-10">
        <div className="mx-auto w-full max-w-[1450px]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className="text-xs font-medium uppercase tracking-[0.14em] text-green-700">For Giggers</span>
              <h2 className="mb-0 mt-2 font-nunito text-[clamp(1.8rem,3.4vw,2.6rem)] font-bold tracking-[-0.015em] text-slate-950">Join as a Gigger</h2>
              <p className="mb-0 mt-3 max-w-3xl text-sm font-normal leading-6 text-slate-600 lg:text-base">A guided path from account creation to verification, finding suitable Gigs and receiving payouts.</p>
            </div>
            <Link to="/provider-register" className="inline-flex w-fit items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-semibold text-white no-underline transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-800 hover:shadow-lg">Become a Gigger <ArrowRight size={17} /></Link>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">{GIGGER_STEPS.map((step, index) => <StepCard key={step.title} step={step} index={index} />)}</div>
        </div>
      </section>

      <section className="bg-[#EAF8F0] px-4 py-11 sm:px-6 sm:py-14 lg:px-8 2xl:px-10">
        <div className="mx-auto max-w-4xl text-center">
          <ShieldCheck size={34} className="mx-auto text-green-700" />
          <h2 className="mb-0 mt-4 font-nunito text-[clamp(1.8rem,3vw,2.5rem)] font-bold text-slate-950">Ready to get started?</h2>
          <p className="mx-auto mb-0 mt-3 max-w-2xl text-sm font-normal leading-6 text-slate-600 lg:text-base">Book local help as a client or start finding flexible work as a Gigger.</p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row"><Link to="/book" className="rounded-xl bg-green-700 px-6 py-3.5 text-sm font-semibold text-white no-underline hover:bg-green-800">Book a Gig</Link><Link to="/provider-register" className="rounded-xl bg-white px-6 py-3.5 text-sm font-semibold text-slate-800 no-underline ring-1 ring-inset ring-slate-200 hover:ring-green-300">Find a Gig</Link></div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default HowItWorks;
